/**
 * ============================================================================
 * COMPOSABLE: Валидация форм
 * ============================================================================
 * Переиспользуемая логика валидации для форм авторизации/регистрации
 * ============================================================================
 */

import { ref, computed } from 'vue'

/**
 * Debounce функция
 * @param {Function} fn - Функция для debounce
 * @param {number} delay - Задержка в мс
 */
export function debounce(fn, delay = 300) {
	let timeoutId = null
	return function (...args) {
		clearTimeout(timeoutId)
		timeoutId = setTimeout(() => fn.apply(this, args), delay)
	}
}

/**
 * Валидаторы полей
 */
export const validators = {
	/**
	 * Проверка email
	 */
	email(value) {
		const trimmed = (value || '').trim()
		if (!trimmed) {
			return 'Введите email'
		}
		if (trimmed.length > 100) {
			return 'Максимум 100 символов'
		}
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
		if (!emailRegex.test(trimmed)) {
			return 'Некорректный email'
		}
		return ''
	},

	/**
	 * Проверка имени пользователя / логина
	 */
	username(value) {
		const trimmed = (value || '').trim()
		if (!trimmed) {
			return 'Введите имя пользователя'
		}
		if (trimmed.length < 3) {
			return 'Минимум 3 символа'
		}
		if (trimmed.length > 50) {
			return 'Максимум 50 символов'
		}
		return ''
	},

	/**
	 * Проверка пароля (базовая, для входа)
	 */
	passwordLogin(value) {
		if (!value) {
			return 'Введите пароль'
		}
		if (value.length < 6) {
			return 'Минимум 6 символов'
		}
		if (value.length > 100) {
			return 'Максимум 100 символов'
		}
		return ''
	},

	/**
	 * Проверка пароля (строгая, для регистрации)
	 */
	passwordRegister(value) {
		if (!value) {
			return 'Введите пароль'
		}
		if (value.length < 8) {
			return 'Минимум 8 символов'
		}
		if (value.length > 100) {
			return 'Максимум 100 символов'
		}
		// Проверка на пробелы в начале/конце
		if (value !== value.trim()) {
			return 'Пароль не должен начинаться или заканчиваться пробелами'
		}
		// Проверка на буквы
		if (!/[a-zA-Zа-яА-ЯёЁ]/.test(value)) {
			return 'Пароль должен содержать буквы'
		}
		// Проверка на цифры
		if (!/\d/.test(value)) {
			return 'Пароль должен содержать цифры'
		}
		// Проверка на спецсимволы (опционально, но рекомендуется)
		// if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)) {
		//   return 'Рекомендуется добавить спецсимволы (!@#$%^&*)'
		// }
		return ''
	},

	/**
	 * Проверка подтверждения пароля
	 */
	confirmPassword(value, password) {
		if (!value) {
			return 'Подтвердите пароль'
		}
		if (value !== password) {
			return 'Пароли не совпадают'
		}
		return ''
	},

	/**
	 * Проверка ФИО
	 */
	lastName(value) {
		const trimmed = (value || '').trim()
		if (!trimmed) {
			return 'Введите фамилию'
		}
		if (trimmed.length < 2) {
			return 'Минимум 2 символа'
		}
		if (trimmed.length > 50) {
			return 'Максимум 50 символов'
		}
		if (!/^[A-Za-zА-Яа-яЁё\s-]+$/.test(trimmed)) {
			return 'Только буквы, пробелы и дефис'
		}
		return ''
	},

	firstName(value) {
		const trimmed = (value || '').trim()
		if (!trimmed) {
			return 'Введите имя'
		}
		if (trimmed.length < 2) {
			return 'Минимум 2 символа'
		}
		if (trimmed.length > 50) {
			return 'Максимум 50 символов'
		}
		if (!/^[A-Za-zА-Яа-яЁё\s-]+$/.test(trimmed)) {
			return 'Только буквы, пробелы и дефис'
		}
		return ''
	},

	patronymic(value) {
		const trimmed = (value || '').trim()
		if (!trimmed) {
			return ''
		}
		if (trimmed.length < 2) {
			return 'Минимум 2 символа'
		}
		if (trimmed.length > 50) {
			return 'Максимум 50 символов'
		}
		if (!/^[A-Za-zА-Яа-яЁё\s-]+$/.test(trimmed)) {
			return 'Только буквы, пробелы и дефис'
		}
		return ''
	},

	/**
	 * Проверка полного имени (ФИО одной строкой)
	 */
	fullName(value) {
		const trimmed = (value || '').trim()
		if (!trimmed) {
			return 'Введите ФИО'
		}
		if (trimmed.length < 5) {
			return 'Минимум 5 символов'
		}
		if (trimmed.length > 150) {
			return 'Максимум 150 символов'
		}
		if (!/^[A-Za-zА-Яа-яЁё\s-]+$/.test(trimmed)) {
			return 'Только буквы, пробелы и дефис'
		}
		// Проверка что есть хотя бы 2 слова (имя и фамилия)
		const words = trimmed.split(/\s+/).filter(Boolean)
		if (words.length < 2) {
			return 'Введите фамилию и имя'
		}
		return ''
	},

}

/**
 * Оценка силы пароля
 * @param {string} password
 * @returns {{ score: number, label: string, color: string }}
 */
export function getPasswordStrength(password) {
	if (!password) {
		return { score: 0, label: '', color: '' }
	}

	let score = 0

	// Длина
	if (password.length >= 8) score += 1
	if (password.length >= 12) score += 1
	if (password.length >= 16) score += 1

	// Разнообразие символов
	if (/[a-z]/.test(password)) score += 1
	if (/[A-Z]/.test(password)) score += 1
	if (/\d/.test(password)) score += 1
	if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) score += 1

	// Определяем уровень
	if (score <= 2) {
		return { score, label: 'Слабый', color: '#ef4444' }
	}
	if (score <= 4) {
		return { score, label: 'Средний', color: '#f59e0b' }
	}
	if (score <= 6) {
		return { score, label: 'Хороший', color: '#22c55e' }
	}
	return { score, label: 'Отличный', color: '#10b981' }
}

/**
 * Composable для формы входа
 */
export function useLoginForm() {
	const username = ref('')
	const password = ref('')
	const rememberMe = ref(false)
	const isSubmitting = ref(false)

	const errors = ref({
		username: '',
		password: '',
	})

	const focused = ref({
		username: false,
		password: false,
	})

	// Trimmed значения (computed)
	const trimmedUsername = computed(() => username.value.trim())

	// Валидация с debounce
	const validateField = debounce((field) => {
		switch (field) {
			case 'username':
				errors.value.username = validators.username(username.value)
				break
			case 'password':
				errors.value.password = validators.passwordLogin(password.value)
				break
		}
	}, 300)

	// Немедленная валидация (для blur и submit)
	const validateFieldImmediate = (field) => {
		switch (field) {
			case 'username':
				errors.value.username = validators.username(username.value)
				break
			case 'password':
				errors.value.password = validators.passwordLogin(password.value)
				break
		}
	}

	// Валидация всей формы
	const validateForm = () => {
		validateFieldImmediate('username')
		validateFieldImmediate('password')

		if (!trimmedUsername.value && !password.value) {
			return 'Пожалуйста, введите логин и пароль'
		}
		if (!trimmedUsername.value) {
			return 'Пожалуйста, введите логин'
		}
		if (!password.value) {
			return 'Пожалуйста, введите пароль'
		}
		if (errors.value.username || errors.value.password) {
			return 'Исправьте ошибки в форме'
		}
		return null
	}

	// Очистка
	const reset = () => {
		username.value = ''
		password.value = ''
		errors.value = { username: '', password: '' }
		focused.value = { username: false, password: false }
	}

	const clearErrors = () => {
		errors.value = { username: '', password: '' }
	}

	return {
		username,
		password,
		rememberMe,
		isSubmitting,
		errors,
		focused,
		trimmedUsername,
		validateField,
		validateFieldImmediate,
		validateForm,
		reset,
		clearErrors,
	}
}

/**
 * Composable для формы регистрации
 */

export function useRegisterForm() {
	const email = ref('')
	const fullName = ref('')
	const lastName = ref('')
	const firstName = ref('')
	const patronymic = ref('')
	const password = ref('')
	const confirmPassword = ref('')
	const isSubmitting = ref(false)

	const errors = ref({
		email: '',
		fullName: '',
		lastName: '',
		firstName: '',
		patronymic: '',
		password: '',
		confirmPassword: '',
	})

	const focused = ref({
		email: false,
		fullName: false,
		lastName: false,
		firstName: false,
		patronymic: false,
		password: false,
		confirmPassword: false,
	})

	// Trimmed значения
	const trimmedEmail = computed(() => email.value.trim().toLowerCase())
	const trimmedLastName = computed(() => lastName.value.trim())
	const trimmedFirstName = computed(() => firstName.value.trim())
	const trimmedPatronymic = computed(() => patronymic.value.trim())
	const trimmedFullName = computed(() =>
		[trimmedLastName.value, trimmedFirstName.value, trimmedPatronymic.value]
			.filter(Boolean)
			.join(' ')
	)

	// Оценка надежности пароля
	const passwordStrength = computed(() => getPasswordStrength(password.value))

	const syncFullName = () => {
		fullName.value = trimmedFullName.value
	}

	// Валидация с debounce
	const validateField = debounce((field) => {
		switch (field) {
			case 'email':
				errors.value.email = validators.email(email.value)
				break
			case 'fullName':
				syncFullName()
				errors.value.fullName = validators.fullName(fullName.value)
				break
			case 'lastName':
				syncFullName()
				errors.value.lastName = validators.lastName(lastName.value)
				break
			case 'firstName':
				syncFullName()
				errors.value.firstName = validators.firstName(firstName.value)
				break
			case 'patronymic':
				syncFullName()
				errors.value.patronymic = validators.patronymic(patronymic.value)
				break
			case 'password':
				errors.value.password = validators.passwordRegister(password.value)
				// Перепроверяем подтверждение при смене пароля
				if (confirmPassword.value) {
					errors.value.confirmPassword = validators.confirmPassword(
						confirmPassword.value,
						password.value
					)
				}
				break
			case 'confirmPassword':
				errors.value.confirmPassword = validators.confirmPassword(
					confirmPassword.value,
					password.value
				)
				break
		}
	}, 300)

	// Немедленная валидация (для blur и submit)
	const validateFieldImmediate = (field) => {
		switch (field) {
			case 'email':
				errors.value.email = validators.email(email.value)
				break
			case 'fullName':
				syncFullName()
				errors.value.fullName = validators.fullName(fullName.value)
				break
			case 'lastName':
				syncFullName()
				errors.value.lastName = validators.lastName(lastName.value)
				break
			case 'firstName':
				syncFullName()
				errors.value.firstName = validators.firstName(firstName.value)
				break
			case 'patronymic':
				syncFullName()
				errors.value.patronymic = validators.patronymic(patronymic.value)
				break
			case 'password':
				errors.value.password = validators.passwordRegister(password.value)
				if (confirmPassword.value) {
					errors.value.confirmPassword = validators.confirmPassword(
						confirmPassword.value,
						password.value
					)
				}
				break
			case 'confirmPassword':
				errors.value.confirmPassword = validators.confirmPassword(
					confirmPassword.value,
					password.value
				)
				break
		}
	}

	// Валидация всей формы
	const validateForm = () => {
		syncFullName()
		validateFieldImmediate('email')
		validateFieldImmediate('fullName')
		validateFieldImmediate('lastName')
		validateFieldImmediate('firstName')
		validateFieldImmediate('patronymic')
		validateFieldImmediate('password')
		validateFieldImmediate('confirmPassword')

		const hasEmptyFields =
			!trimmedEmail.value ||
			!fullName.value.trim() ||
			!password.value ||
			!confirmPassword.value

		if (hasEmptyFields) {
			return 'Пожалуйста, заполните все поля формы'
		}

		const hasErrors =
			errors.value.email ||
			errors.value.fullName ||
			errors.value.lastName ||
			errors.value.firstName ||
			errors.value.patronymic ||
			errors.value.password ||
			errors.value.confirmPassword

		if (hasErrors) {
			return 'Исправьте ошибки в форме'
		}

		return null
	}

	// Сброс
	const reset = () => {
		email.value = ''
		fullName.value = ''
		lastName.value = ''
		firstName.value = ''
		patronymic.value = ''
		password.value = ''
		confirmPassword.value = ''
		errors.value = {
			email: '',
			fullName: '',
			lastName: '',
			firstName: '',
			patronymic: '',
			password: '',
			confirmPassword: '',
		}
		focused.value = {
			email: false,
			fullName: false,
			lastName: false,
			firstName: false,
			patronymic: false,
			password: false,
			confirmPassword: false,
		}
	}

	const clearErrors = () => {
		errors.value = {
			email: '',
			fullName: '',
			lastName: '',
			firstName: '',
			patronymic: '',
			password: '',
			confirmPassword: '',
		}
	}

	// Очистка паролей (например, после успешной регистрации)
	const clearPasswords = () => {
		password.value = ''
		confirmPassword.value = ''
	}

	return {
		email,
		fullName,
		lastName,
		firstName,
		patronymic,
		password,
		confirmPassword,
		isSubmitting,
		errors,
		focused,
		trimmedEmail,
		trimmedLastName,
		trimmedFirstName,
		trimmedPatronymic,
		trimmedFullName,
		passwordStrength,
		validateField,
		validateFieldImmediate,
		validateForm,
		reset,
		clearErrors,
		clearPasswords,
	}
}

export default {
	debounce,
	validators,
	getPasswordStrength,
	useLoginForm,
	useRegisterForm,
}
